import {v4 as uuidv4} from 'uuid';
import {canonicalEdgeBetween} from './graphModel';

const VALID_TYPES = new Set(['database', 'application', 'service', 'file', 'api', 'other']);
const GRID_COLS = 4;
const GRID_DX = 220;
const GRID_DY = 120;

const cf = (s) => (s || '').trim().toLowerCase();

/**
 * Compute a start-X and origin-Y for new nodes based on the bounding box of existing nodes.
 * Returns {startX, originY}.
 */
const newNodeOrigin = (existingNodes) => {
  const appNodes = existingNodes.filter((n) => n.kind === 'app');
  if (appNodes.length === 0) return {startX: 80, originY: 80};
  const xs = appNodes.map((n) => (n.position && n.position.x) || 0);
  const ys = appNodes.map((n) => (n.position && n.position.y) || 0);
  const ws = appNodes.map((n) => n.width || 172);
  return {
    startX: Math.max(...xs.map((x, i) => x + ws[i])) + 200,
    originY: Math.min(...ys),
  };
};

/**
 * Lay out an array of staged nodes in a grid starting at (startX, originY).
 * Mutates each node's position in place (they are freshly constructed objects).
 */
const assignPositions = (stagedNodes, startX, originY) => {
  stagedNodes.forEach((node, i) => {
    const col = Math.floor(i / GRID_COLS);
    const row = i % GRID_COLS;
    node.position = {x: startX + col * GRID_DX, y: (originY || 80) + row * GRID_DY};
  });
};

/**
 * Ensure a nodeUpserts entry exists for nodeId in the upserts map.
 * Returns the entry.
 */
const ensureUpsert = (nodeUpsertsMap, nodeId) => {
  if (!nodeUpsertsMap.has(nodeId)) {
    nodeUpsertsMap.set(nodeId, {nodeId, tablesToAdd: [], elementsToAdd: []});
  }
  return nodeUpsertsMap.get(nodeId);
};

/**
 * Resolve or synthesize a table slot on a node (existing or staged).
 * Returns the table id. Stages new tables into the upsert for that node if needed.
 * `existingNodeData` is node.data from map.nodes (may be undefined for staged nodes).
 * `stagedNodeData` holds the staged node's data object (for new nodes).
 * `stagedTablesIndex` is a Map keyed by `${nodeId}:${cf(name)}` → table id.
 */
const resolveTable = (nodeId, name, existingNodeData, stagedNodeData, nodeUpsertsMap, stagedTablesIndex) => {
  const key = `${nodeId}:${cf(name)}`;
  if (stagedTablesIndex.has(key)) return stagedTablesIndex.get(key);

  const allTables = [
    ...((existingNodeData && existingNodeData.tables) || []),
    ...((stagedNodeData && stagedNodeData.tables) || []),
    ...(nodeUpsertsMap.has(nodeId) ? nodeUpsertsMap.get(nodeId).tablesToAdd : []),
  ];
  const existing = allTables.find((t) => cf(t.name) === cf(name));
  if (existing) {
    stagedTablesIndex.set(key, existing.id);
    return existing.id;
  }

  const tblId = `tbl-${uuidv4()}`;
  stagedTablesIndex.set(key, tblId);
  ensureUpsert(nodeUpsertsMap, nodeId).tablesToAdd.push({id: tblId, name, dataElements: []});
  return tblId;
};

/**
 * Resolve or synthesize an element within a table scope (or node-level when tableId is null).
 * Returns {elementId, errors:[]} where errors may contain a single ambiguity error.
 */
const resolveElement = (
    nodeId, tableId, name, existingNodeData, stagedNodeData,
    nodeUpsertsMap, stagedTablesIndex, stagedElementsIndex, row,
) => {
  const scopeKey = tableId || `__node__${nodeId}`;
  const elKey = `${nodeId}:${scopeKey}:${cf(name)}`;

  if (stagedElementsIndex.has(elKey)) return {elementId: stagedElementsIndex.get(elKey), errors: []};

  // Gather elements in scope from existing + staged
  let scopeElements = [];
  if (tableId) {
    const allTables = [
      ...((existingNodeData && existingNodeData.tables) || []),
      ...((stagedNodeData && stagedNodeData.tables) || []),
      ...(nodeUpsertsMap.has(nodeId) ? nodeUpsertsMap.get(nodeId).tablesToAdd : []),
    ];
    const tbl = allTables.find((t) => t.id === tableId);
    scopeElements = (tbl && tbl.dataElements) || [];
  } else {
    scopeElements = [
      ...((existingNodeData && existingNodeData.dataElements) || []),
      ...((stagedNodeData && stagedNodeData.dataElements) || []),
      ...(nodeUpsertsMap.has(nodeId) ? nodeUpsertsMap.get(nodeId).elementsToAdd : []),
    ];
  }

  const matches = scopeElements.filter(
      (el) => cf(el.name) === cf(name) || cf(el.columnName) === cf(name),
  );
  if (matches.length >= 2) {
    return {elementId: null, errors: [{row, level: 'error', msg: `ambiguous element '${name}'`}]};
  }
  if (matches.length === 1) {
    stagedElementsIndex.set(elKey, matches[0].id);
    return {elementId: matches[0].id, errors: []};
  }

  const elId = `el-${uuidv4()}`;
  stagedElementsIndex.set(elKey, elId);
  const newEl = {id: elId, name, displayName: name, dataType: ''};
  ensureUpsert(nodeUpsertsMap, nodeId);
  if (tableId) {
    const upsert = nodeUpsertsMap.get(nodeId);
    const tbl = upsert.tablesToAdd.find((t) => t.id === tableId);
    if (tbl) {
      tbl.dataElements.push(newEl);
    }
  } else {
    nodeUpsertsMap.get(nodeId).elementsToAdd.push(newEl);
  }
  return {elementId: elId, errors: []};
};

/**
 * Pure normalizer. Converts a flat array of import rows into a structured payload
 * ready for the IMPORT_LINEAGE reducer case.
 *
 * @param {object[]} rows   Rows from POST /lineage/import (each has sourceSystem, sourceType, etc.)
 * @param {object}   map    Current lineage map ({nodes, edges, mappings})
 * @param {object}   opts   {skipInvalid: true}
 * @returns {{payload, summary, errors}}
 */
export const normalizeImport = (rows, map, opts = {skipInvalid: true}) => {
  const {skipInvalid = true} = opts;

  const payload = {
    nodesToAdd: [],
    nodeUpserts: [],
    edgesToAdd: [],
    edgeUpdates: [],
    mappingsToAdd: [],
    mappingUpdates: [],
  };

  const errors = [];

  // Per-run indexes
  const stagedNodesByName = new Map(); // cf(name) → staged node object (reference)
  const nodeUpsertsMap = new Map(); // nodeId → {nodeId, tablesToAdd, elementsToAdd}
  const stagedTablesIndex = new Map(); // `${nodeId}:${cf(name)}` → tableId
  const stagedElementsIndex = new Map(); // `${nodeId}:${scopeKey}:${cf(name)}` → elementId
  const pendingEdges = new Map(); // `${minId}:${maxId}` → edgeId
  const edgeUpdatesSeen = new Set(); // edgeIds already queued for direction update
  const mappingUpdatesSeen = new Set(); // mapping ids already queued for direction update

  // Build mapping dedup key sets from existing mappings (both orientations)
  const existingMappingKeys = new Set();
  (map.mappings || []).forEach((m) => {
    const fwd = `${m.sourceNodeId}|${m.sourceTableId || ''}|${m.sourceElementId}||${m.targetNodeId}|${m.targetTableId || ''}|${m.targetElementId}`;
    const rev = `${m.targetNodeId}|${m.targetTableId || ''}|${m.targetElementId}||${m.sourceNodeId}|${m.sourceTableId || ''}|${m.sourceElementId}`;
    existingMappingKeys.add(fwd);
    existingMappingKeys.add(rev);
  });
  // map from fwd key → existing mapping id (for mappingUpdates)
  const existingMappingByFwd = new Map();
  (map.mappings || []).forEach((m) => {
    const fwd = `${m.sourceNodeId}|${m.sourceTableId || ''}|${m.sourceElementId}||${m.targetNodeId}|${m.targetTableId || ''}|${m.targetElementId}`;
    existingMappingByFwd.set(fwd, m.id);
  });

  // Staged mappings dedup keys → staged mapping object (for in-batch idempotency)
  const stagedMappingByFwd = new Map();

  // Compute bounding box for new node positioning (run once, lazy)
  let originComputed = false;
  let newNodeStartX = 80;
  let newNodeOriginY = 80;
  const ensureOrigin = () => {
    if (originComputed) return;
    originComputed = true;
    const o = newNodeOrigin(map.nodes || []);
    newNodeStartX = o.startX;
    newNodeOriginY = o.originY;
  };

  // --- Node resolution ---
  const resolveNode = (name, typehint, rowNum) => {
    if (!name || !name.trim()) return {nodeId: null, nodeErrors: []};
    const cfName = cf(name);
    const trimmedName = name.trim();

    // Check if already staged in this run
    if (stagedNodesByName.has(cfName)) {
      const staged = stagedNodesByName.get(cfName);
      if (typehint && typehint.trim() && cf(staged.data.appType) !== cf(typehint.trim())) {
        const validType = VALID_TYPES.has(cf(typehint.trim())) ? cf(typehint.trim()) : null;
        if (validType && validType !== cf(staged.data.appType)) {
          errors.push({row: rowNum, level: 'warn', msg: `conflicting type for '${trimmedName}'`});
        }
      }
      return {nodeId: staged.id, nodeErrors: []};
    }

    // Search existing nodes (kind === 'app' only)
    const matches = (map.nodes || []).filter((n) => n.kind === 'app' && cf(n.data && n.data.label) === cfName);
    if (matches.length >= 2) {
      return {nodeId: null, nodeErrors: [{row: rowNum, level: 'error', msg: `ambiguous node name '${trimmedName}'`}]};
    }
    if (matches.length === 1) {
      return {nodeId: matches[0].id, nodeErrors: []};
    }

    // Stage a new node
    ensureOrigin();
    const appType = (typehint && VALID_TYPES.has(cf(typehint.trim()))) ? cf(typehint.trim()) : 'application';
    const newNode = {
      id: `node-${uuidv4()}`,
      kind: 'app',
      type: 'appNode',
      position: {x: 0, y: 0}, // assigned later by assignPositions
      data: {label: trimmedName, appType, description: '', details: {}, tables: [], dataElements: []},
    };
    stagedNodesByName.set(cfName, newNode);
    payload.nodesToAdd.push(newNode);
    return {nodeId: newNode.id, nodeErrors: []};
  };

  // --- Edge ensure ---
  const ensureEdge = (srcNodeId, tgtNodeId, direction, label) => {
    const canonical = canonicalEdgeBetween(map.edges || [], srcNodeId, tgtNodeId);
    if (canonical) {
      if (canonical.direction !== direction && canonical.direction !== 'bidirectional') {
        if (!edgeUpdatesSeen.has(canonical.id)) {
          edgeUpdatesSeen.add(canonical.id);
          payload.edgeUpdates.push({id: canonical.id, direction: 'bidirectional'});
        }
      }
      return canonical.id;
    }

    const pairKey = [srcNodeId, tgtNodeId].sort().join(':');
    if (pendingEdges.has(pairKey)) return pendingEdges.get(pairKey);

    const edgeId = `edge-${uuidv4()}`;
    const validDir = VALID_DIRECTIONS.has(direction) ? direction : 'forward';
    payload.edgesToAdd.push({
      id: edgeId,
      source: srcNodeId,
      target: tgtNodeId,
      sourceHandle: null,
      targetHandle: null,
      direction: validDir,
      label: label || '',
      data: {autoCreated: true},
    });
    pendingEdges.set(pairKey, edgeId);
    return edgeId;
  };

  // --- Mapping ensure (idempotent) ---
  const ensureMapping = (srcNodeId, srcTableId, srcElementId, tgtNodeId, tgtTableId, tgtElementId, edgeId, direction, transformation) => {
    const fwdKey = `${srcNodeId}|${srcTableId || ''}|${srcElementId}||${tgtNodeId}|${tgtTableId || ''}|${tgtElementId}`;
    const revKey = `${tgtNodeId}|${tgtTableId || ''}|${tgtElementId}||${srcNodeId}|${srcTableId || ''}|${srcElementId}`;

    // Same orientation: already exists in map or batch → skip
    if (existingMappingKeys.has(fwdKey)) return;
    if (stagedMappingByFwd.has(fwdKey)) return;

    // Reverse orientation exists in map → push mappingUpdate once
    if (existingMappingKeys.has(revKey)) {
      const existingId = existingMappingByFwd.get(revKey);
      if (existingId && !mappingUpdatesSeen.has(existingId)) {
        mappingUpdatesSeen.add(existingId);
        payload.mappingUpdates.push({id: existingId, direction: 'bidirectional'});
      }
      return;
    }

    // Reverse orientation staged in batch → push mappingUpdate once
    if (stagedMappingByFwd.has(revKey)) {
      const stagedMapping = stagedMappingByFwd.get(revKey);
      if (!mappingUpdatesSeen.has(stagedMapping.id)) {
        mappingUpdatesSeen.add(stagedMapping.id);
        payload.mappingUpdates.push({id: stagedMapping.id, direction: 'bidirectional'});
      }
      return;
    }

    const validDir = VALID_DIRECTIONS.has(direction) ? direction : 'forward';
    const newMapping = {
      id: `map-${uuidv4()}`,
      edgeId,
      sourceNodeId: srcNodeId,
      sourceTableId: srcTableId || null,
      sourceElementId: srcElementId,
      targetNodeId: tgtNodeId,
      targetTableId: tgtTableId || null,
      targetElementId: tgtElementId,
      direction: validDir,
      transformation: transformation || '',
      label: '',
    };
    payload.mappingsToAdd.push(newMapping);
    stagedMappingByFwd.set(fwdKey, newMapping);
    existingMappingKeys.add(fwdKey);
    existingMappingKeys.add(revKey);
  };

  const VALID_DIRECTIONS = new Set(['forward', 'backward', 'bidirectional']);

  let skippedCount = 0;

  rows.forEach((row) => {
    const rowNum = row._row;
    const srcName = (row.sourceSystem || '').trim();
    const tgtName = (row.targetSystem || '').trim();
    const srcType = (row.sourceType || '').trim();
    const tgtType = (row.targetType || '').trim();
    const srcTableName = (row.sourceTable || '').trim();
    const tgtTableName = (row.targetTable || '').trim();
    const srcFieldName = (row.sourceField || '').trim();
    const tgtFieldName = (row.targetField || '').trim();
    const direction = VALID_DIRECTIONS.has((row.direction || '').trim().toLowerCase()) ?
      (row.direction || '').trim().toLowerCase() : 'forward';
    const transformation = (row.transformation || '').trim();
    const label = (row.label || '').trim();

    if (!srcName || !tgtName) {
      errors.push({row: rowNum, level: 'error', msg: 'Source System and Target System are required'});
      if (skipInvalid) {
        skippedCount++; return;
      }
    }

    // Resolve nodes
    const {nodeId: srcNodeId, nodeErrors: srcNodeErrors} = resolveNode(srcName, srcType, rowNum);
    const {nodeId: tgtNodeId, nodeErrors: tgtNodeErrors} = resolveNode(tgtName, tgtType, rowNum);

    const nodeErrors = [...srcNodeErrors, ...tgtNodeErrors];
    if (nodeErrors.length) {
      nodeErrors.forEach((e) => errors.push(e));
      if (skipInvalid) {
        skippedCount++; return;
      }
    }
    if (!srcNodeId || !tgtNodeId) {
      if (skipInvalid) {
        skippedCount++; return;
      }
    }

    const hasSrcField = !!srcFieldName;
    const hasTgtField = !!tgtFieldName;
    const hasSrcTable = !!srcTableName;
    const hasTgtTable = !!tgtTableName;

    // Validate field pair
    if (hasSrcField !== hasTgtField) {
      errors.push({row: rowNum, level: 'error', msg: 'provide both Source Field and Target Field, or neither'});
      if (skipInvalid) {
        skippedCount++; return;
      }
    }

    // Self-link check for pure system link rows
    if (!hasSrcField && !hasTgtField && !hasSrcTable && !hasTgtTable) {
      if (srcNodeId === tgtNodeId) {
        errors.push({row: rowNum, level: 'error', msg: `a system can't link to itself`});
        if (skipInvalid) {
          skippedCount++; return;
        }
      }
      ensureEdge(srcNodeId, tgtNodeId, direction, label);
      return;
    }

    const existingSrcNodeData = (() => {
      const n = (map.nodes || []).find((x) => x.id === srcNodeId);
      return n ? n.data : null;
    })();
    const existingTgtNodeData = (() => {
      const n = (map.nodes || []).find((x) => x.id === tgtNodeId);
      return n ? n.data : null;
    })();
    const stagedSrcNodeData = stagedNodesByName.get(cf(srcName)) ? stagedNodesByName.get(cf(srcName)).data : null;
    const stagedTgtNodeData = stagedNodesByName.get(cf(tgtName)) ? stagedNodesByName.get(cf(tgtName)).data : null;

    if (!hasSrcField && !hasTgtField) {
      // Table-only rows: register the table slots and ensure the system edge
      if (hasSrcTable) {
        resolveTable(srcNodeId, srcTableName, existingSrcNodeData, stagedSrcNodeData, nodeUpsertsMap, stagedTablesIndex);
      }
      if (hasTgtTable) {
        resolveTable(tgtNodeId, tgtTableName, existingTgtNodeData, stagedTgtNodeData, nodeUpsertsMap, stagedTablesIndex);
      }
      if (srcNodeId !== tgtNodeId) {
        ensureEdge(srcNodeId, tgtNodeId, direction, label);
      }
      return;
    }

    // Field mapping row
    let srcTableId = null;
    let tgtTableId = null;

    if (hasSrcTable) {
      srcTableId = resolveTable(srcNodeId, srcTableName, existingSrcNodeData, stagedSrcNodeData, nodeUpsertsMap, stagedTablesIndex);
    }
    if (hasTgtTable) {
      tgtTableId = resolveTable(tgtNodeId, tgtTableName, existingTgtNodeData, stagedTgtNodeData, nodeUpsertsMap, stagedTablesIndex);
    }

    const {elementId: srcElementId, errors: srcElErrors} = resolveElement(
        srcNodeId, srcTableId, srcFieldName,
        existingSrcNodeData, stagedSrcNodeData,
        nodeUpsertsMap, stagedTablesIndex, stagedElementsIndex, rowNum,
    );
    const {elementId: tgtElementId, errors: tgtElErrors} = resolveElement(
        tgtNodeId, tgtTableId, tgtFieldName,
        existingTgtNodeData, stagedTgtNodeData,
        nodeUpsertsMap, stagedTablesIndex, stagedElementsIndex, rowNum,
    );

    const elErrors = [...srcElErrors, ...tgtElErrors];
    if (elErrors.length) {
      elErrors.forEach((e) => errors.push(e));
      if (skipInvalid) {
        skippedCount++; return;
      }
    }
    if (!srcElementId || !tgtElementId) {
      if (skipInvalid) {
        skippedCount++; return;
      }
    }

    // An element cannot map to itself (mirrors connectElements' sameEl guard) — the
    // canvas can't create this by hand, so reject it on import too.
    if (srcNodeId === tgtNodeId && (srcTableId || null) === (tgtTableId || null) && srcElementId === tgtElementId) {
      errors.push({row: rowNum, level: 'error', msg: 'an element cannot map to itself'});
      if (skipInvalid) {
        skippedCount++; return;
      }
    }

    let edgeId;
    if (srcNodeId === tgtNodeId) {
      edgeId = `self-${srcNodeId}`;
    } else {
      edgeId = ensureEdge(srcNodeId, tgtNodeId, direction, label);
    }

    ensureMapping(srcNodeId, srcTableId, srcElementId, tgtNodeId, tgtTableId, tgtElementId, edgeId, direction, transformation);
  });

  // Bake positions onto staged nodes (done after all rows processed so we know the count)
  if (payload.nodesToAdd.length) {
    ensureOrigin();
    assignPositions(payload.nodesToAdd, newNodeStartX, newNodeOriginY);
  }

  // Flatten nodeUpserts map to array
  payload.nodeUpserts = [...nodeUpsertsMap.values()];

  const summary = {
    nodeCount: payload.nodesToAdd.length,
    edgeCount: payload.edgesToAdd.length,
    mappingCount: payload.mappingsToAdd.length,
    skippedCount,
  };

  return {payload, summary, errors};
};
